<template>
  <div>
    <!-- Header -->
    <AppHeader />

    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Загрузка профиля преподавателя...</p>
    </div>

    <!-- Error state -->
    <div v-if="error" class="error-state">
      <p>{{ error }}</p>
      <router-link to="/teachers" class="retry-button">Вернуться к списку преподавателей</router-link>
    </div>

    <!-- Teacher Profile -->
    <div v-if="teacher && !loading && !error" class="teacher-profile">
      <UISection class="profile-section">
        <template #default>
          <router-link to="/teachers" class="profile-back"><span aria-hidden="true">←</span> Все преподаватели</router-link>
          <div class="profile-card">
            <div class="profile-media">
              <div class="profile-avatar" v-if="teacher.photo">
                <img :src="teacher.photo" :alt="teacher.name" decoding="async" />
              </div>
              <div class="profile-avatar profile-avatar--placeholder" v-else>
                <i class="fas fa-user" aria-hidden="true"></i>
              </div>
              <UIButton to="/contacts#contact-methods" size="sm">Записаться на урок</UIButton>
            </div>
            <div class="profile-content">
              <div class="profile-heading">
                <span class="profile-eyebrow">Преподаватель китайского языка</span>
                <h1>{{ teacher.name }}</h1>
                <p v-if="teacher.label">{{ teacher.label }}</p>
              </div>
              <section class="profile-experience" v-if="achievements.length" aria-labelledby="experience-title">
                <h2 id="experience-title">Образование и опыт</h2>
                <ul class="profile-achievements">
                  <li class="profile-achievement" v-for="(achievement, index) in achievements" :key="index">
                    <span class="achievement-number" aria-hidden="true">{{ String(index + 1).padStart(2, '0') }}</span>
                    <p>{{ achievement }}</p>
                  </li>
                </ul>
              </section>
              <div class="profile-contacts" v-if="teacher.email || teacher.social_link">
                <div class="profile-contact-item" v-if="teacher.email">
                  <i class="fas fa-envelope"></i>
                  <a :href="`mailto:${teacher.email}`">{{ teacher.email }}</a>
                </div>
                <div class="profile-contact-item" v-if="teacher.social_link">
                  <i class="fas fa-link"></i>
                  <a :href="teacher.social_link" target="_blank" rel="noopener noreferrer">{{ teacher.social_link }}</a>
                </div>
              </div>
            </div>
          </div>
        </template>
      </UISection>
    </div>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <h2>Готовы начать изучение китайского?</h2>
        <p>Свяжитесь с нами и получите бесплатный пробный урок</p>
        <UIButton to="/contacts#contact-methods" variant="outline" class="cta-button">Связаться с нами</UIButton>
      </div>
    </section>

    <!-- Footer -->
    <UIFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import UIButton from '@/components/ui/UIButton.vue'
import UISection from '@/components/ui/UISection.vue'
import UIFooter from '@/components/ui/UIFooter.vue'
import { addSchemaScript, getPersonSchema, getBreadcrumbSchema, clearAllSchemas } from '@/utils/schema'

export default {
  name: 'TeacherProfilePage',
  components: {
    AppHeader,
    UIButton,
    UISection,
    UIFooter
  },
  data() {
    return {
      teacher: null,
      loading: true,
      error: null
    }
  },
  computed: {
    achievements() {
      return (this.teacher?.achievements || []).filter(item => typeof item === 'string' && item.trim())
    }
  },
  async mounted() {
    await this.fetchTeacher()
  },
  methods: {
    async fetchTeacher() {
      try {
        this.loading = true;
        const teacherId = this.$route.params.id;
        const response = await fetch(`/api/v1/public/teachers/${teacherId}`);
        const data = await response.json();
        
        if (response.ok) {
          // Оставляем только имя (без фамилии)
          const firstName = data.first_name?.trim();
          this.teacher = {
            ...data,
            name: firstName || data.name || ''
          };
          
          // Обновляем заголовок страницы
          const teacherName = `${data.first_name || ''} ${data.last_name || ''}`.trim() || data.name || 'Преподаватель';
          document.title = `${teacherName} - Преподаватель - Мама я в Китае`;
          // Обновляем мета-тег description
          let metaDescription = document.querySelector('meta[name="description"]');
          if (metaDescription && data.bio) {
            const shortBio = data.bio.substring(0, 160).trim();
            metaDescription.setAttribute('content', shortBio || `Преподаватель китайского языка: ${teacherName}`);
          }
          // Обновляем Open Graph теги
          let ogTitle = document.querySelector('meta[property="og:title"]');
          if (ogTitle) {
            ogTitle.setAttribute('content', `${teacherName} - Преподаватель - Мама я в Китае`);
          }
          
          // Добавляем Schema.org разметку для Яндекс
          clearAllSchemas()
          const personSchema = getPersonSchema(data);
          if (personSchema) {
            addSchemaScript(personSchema, 'person');
          }
          
          // Добавляем хлебные крошки
          const breadcrumbs = getBreadcrumbSchema([
            { name: 'Главная', url: window.location.origin },
            { name: 'Преподаватели', url: window.location.origin + '/teachers' },
            { name: teacherName, url: window.location.href }
          ]);
          addSchemaScript(breadcrumbs, 'breadcrumbs');
        } else {
          this.error = 'Учитель не найден';
        }
      } catch (error) {
        this.error = 'Ошибка соединения с сервером';
        console.error('Error fetching teacher:', error);
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.nav-menu a.active {
  color: #e74c3c;
}

.loading-state, .error-state {
  text-align: center;
  padding: 4rem 0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #e74c3c;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.retry-button {
  display: inline-block;
  background: #e74c3c;
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 6px;
  text-decoration: none;
  margin-top: 1rem;
  transition: background 0.3s ease;
}

.retry-button:hover {
  background: #c0392b;
}

.teacher-profile {
  padding: 132px 0 72px;
  background: #f7f5f1;
  color: #302e2b;
}
.profile-section { padding: 0; }
.profile-back {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 28px;
  color: #70665f;
  font-size: 0.9rem;
  text-decoration: none;
}
.profile-back:hover { color: #833c3f; }
.profile-card {
  display: grid;
  grid-template-columns: minmax(220px, 320px) minmax(0, 1fr);
  align-items: start;
  gap: clamp(28px, 5vw, 64px);
  padding: clamp(24px, 4vw, 48px);
  background: #fffefa;
  border: 1px solid #e7e1d8;
  border-radius: 20px;
  box-shadow: 0 16px 48px -32px rgba(55, 43, 30, 0.22);
}
.profile-media { display: grid; gap: 20px; min-width: 0; }
.profile-avatar {
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: 12px;
  overflow: hidden;
  background: #eee9e1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.profile-avatar img { width: 100%; height: 100%; object-fit: cover; object-position: center 30%; }
.profile-avatar--placeholder { color: #9c8d7e; font-size: 4rem; }
.profile-media .btn {
  justify-content: center;
  padding: 14px 16px;
  background: #833c3f;
  border-color: #833c3f;
  border-radius: 8px;
  box-shadow: none;
  font-size: 0.95rem;
  text-align: center;
}
.profile-media .btn:hover { background: #692e31; }
.profile-content { min-width: 0; display: grid; align-content: start; gap: 32px; }
.profile-eyebrow { display: block; color: #856d54; font-size: 0.72rem; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; }
.profile-heading h1 { margin: 12px 0; font-size: clamp(2.25rem, 4vw, 3.5rem); font-weight: 500; line-height: 1.15; overflow-wrap: anywhere; }
.profile-heading p { margin: 0; font-size: 1rem; color: #746d65; overflow-wrap: anywhere; }
.profile-experience h2 { margin: 0 0 8px; font-size: 0.8rem; color: #766957; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; }
.profile-achievements { list-style: none; margin: 0; padding: 0; }
.profile-achievement { display: grid; grid-template-columns: 24px minmax(0, 1fr); align-items: start; gap: 16px; padding: 20px 0; border-bottom: 1px solid #eae5dd; }
.profile-achievement:last-child { border-bottom: 0; padding-bottom: 0; }
.achievement-number { padding-top: 3px; color: #99816a; font-size: 0.75rem; font-variant-numeric: tabular-nums; }
.profile-achievement p { margin: 0; font-size: 0.98rem; line-height: 1.8; white-space: pre-line; overflow-wrap: anywhere; }
.profile-contacts { display: grid; gap: 12px; padding-top: 20px; border-top: 1px solid #eae5dd; }
.profile-contact-item { display: flex; align-items: baseline; gap: 12px; min-width: 0; font-size: 0.9rem; }
.profile-contact-item i { flex: 0 0 18px; color: #99816a; }
.profile-contact-item a { color: #833c3f; overflow-wrap: anywhere; text-underline-offset: 4px; }
.cta-section { background: #302e2b; }
.cta-section h2 { font-size: clamp(1.65rem, 3vw, 2.5rem); line-height: 1.3; overflow-wrap: anywhere; }
.cta-section .cta-button { color: #fff8ee; border-color: #b8a18a; box-shadow: none; }
.cta-section .cta-button:hover { background: #51483f; }
@media (max-width: 700px) {
  .teacher-profile { padding: 104px 0 40px; }
  .profile-back { margin-bottom: 20px; }
  .profile-card { grid-template-columns: minmax(0, 1fr); gap: 28px; padding: 20px; border-radius: 16px; }
  .profile-media { width: 100%; max-width: 320px; justify-self: center; gap: 16px; }
  .profile-avatar { aspect-ratio: 4 / 5; }
  .profile-content { gap: 28px; }
  .profile-heading h1 { font-size: 2.25rem; }
  .profile-achievement { gap: 12px; padding: 18px 0; }
  .profile-achievement p { font-size: 0.95rem; line-height: 1.75; }
}
</style>
